import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { TWclassNames } from "../../utils/Div";

const PROJECT_PLAN_PATH = /\/dashboard\/projects\/(?:plan|[^/]+\/plan)(?:\/|$)/;

function isTabActive(id, pathname) {
  if (id === "calendar") return pathname.startsWith("/dashboard/calendar");
  if (id === "project_plan") return PROJECT_PLAN_PATH.test(pathname);
  if (id === "crew") return pathname.startsWith("/dashboard/crew");
  if (id === "resource_plan") return pathname.startsWith("/dashboard/hours/resource-plan");
  return false;
}

/**
 * Desktop-only jump tabs between calendar-style planning views.
 * Hidden below the `lg` breakpoint (1024px).
 *
 * Each tab uses the same role + company `active_modules` gate as its dashboard
 * route (`useUserHasAccess` / `RoleOnly`). No tab without both user rights and
 * a subscribed module.
 */
export function PlanningQuickViewTabs() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const canCalendar = useUserHasAccess({
    role: "calendar",
    feature: "calendar",
  });
  const canProjectPlan = useUserHasAccess({
    roles: ["projects_plan"],
    feature: "projects",
  });
  const canCrew = useUserHasAccess({
    roles: ["projects_plan", "hours", "edit_hours", "hours_approval"],
    feature: "crew_scheduling",
  });
  const canResourcePlan = useUserHasAccess({
    role: "hours",
    action: "read",
    feature: "hours",
  });

  const tabs = [
    {
      id: "calendar",
      to: "/dashboard/calendar",
      label: t("calendar.quick_views.calendar"),
      show: canCalendar,
    },
    {
      id: "project_plan",
      to: "/dashboard/projects/plan",
      label: t("calendar.quick_views.project_plan"),
      show: canProjectPlan,
    },
    {
      id: "crew",
      to: "/dashboard/crew",
      label: t("calendar.quick_views.crew"),
      show: canCrew,
    },
    {
      id: "resource_plan",
      to: "/dashboard/hours/resource-plan",
      label: t("calendar.quick_views.resource_plan"),
      show: canResourcePlan,
    },
  ].filter((tab) => tab.show);

  if (tabs.length < 2) return null;

  return (
    <nav
      className="mb-0 hidden lg:flex flex-wrap items-center gap-1"
      aria-label={t("calendar.quick_views.aria")}
    >
      {tabs.map((tab) => {
        const active = isTabActive(tab.id, pathname);
        return (
          <NavLink
            key={tab.id}
            to={tab.to}
            className={TWclassNames(
              "rounded-full px-3 py-1.5 text-sm font-semibold transition-colors",
              active
                ? "bg-[#1463C7] text-white"
                : "bg-white text-[#475569] ring-1 ring-inset ring-[#E2E8F0] hover:bg-[#F8FAFC] hover:text-[#1463C7]"
            )}
            aria-current={active ? "page" : undefined}
          >
            {tab.label}
          </NavLink>
        );
      })}
    </nav>
  );
}
